import { trainingAPI } from "@api/TrainingAPI"
import type { Dayjs } from "dayjs"
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { Button, Col, InputNumber, Row, Select, Spin, Tag, Typography } from 'antd';
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { PlanTable } from "@components/Common/PlanTable/PlanTable";
import "../../../Styles/styles-misc.css";

const { Text } = Typography;

export const SecondStep = ({
    startDate,
    endDate,
    days,
    players,
    playerLabels,
    plan,
    setPlan,
    setDrills,
    setCostPerMin: setCostPerMinParent,
    drillData
}: ISecondStep) => {
    const [costPerMin, setCostPerMin] = useState<DrillCostPerMinute[]>([])
    const [loading, setLoading] = useState(true)
    const [selectedDrill, setSelectedDrill] = useState<number | undefined>(undefined)
    const [selectedDuration, setSelectedDuration] = useState<number>(1)

    const { metrics: teamMetrics, info: teamInfo } = useSelector(({ team }: any) => team)

    useEffect(() => {
        setLoading(true);
        (async () => {
            try {
                const result: DrillCostPerMinute[] = await (trainingAPI as any).getDrillCostPerMin(
                    players, startDate.format("YYYY-MM-DD"), endDate.format("YYYY-MM-DD"), days
                )
                setCostPerMin(result)
                setCostPerMinParent(result)
                setDrills(result.map(d => ({ drill_id: d.drill_id, name: d.drill_id__name })))
            } catch (error) {
                console.error("Error loading drill cost data:", error);
                message.error("Error loading drill cost data");
            } finally {
                setLoading(false)
            }
        })()
    }, [])

    const drills = costPerMin.map(d => ({ drill_id: d.drill_id, name: d.drill_id__name }))

    const handleAddPlan = () => {
        if (selectedDrill && selectedDuration) {
            setPlan([...plan, { drill_id: selectedDrill, duration: selectedDuration }])
            setSelectedDrill(undefined)
            setSelectedDuration(1)
        }
    }

    const handleRemovePlan = (index: number) => {
        setPlan(plan.filter((_, i) => i !== index))
    }

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px' }}>Loading drill data...</div>
            </div>
        )
    }

    return (
        <div style={{ marginBottom: '20px' }}>
            <Row style={{ marginBottom: '10px', rowGap: '4px' }}>
                <Text strong>Filters Applied:</Text>
                <Tag color="default" style={{ marginLeft: '2px', marginRight: '4px' }}>
                    {startDate.format("YYYY-MM-DD")} - {endDate.format("YYYY-MM-DD")}
                </Tag>
                {days.length !== 0 &&
                    <Tag color="default" style={{ marginRight: '4px' }}>
                        Include: {days.join(", ")}
                    </Tag>
                }
                {playerLabels.length !== 0 &&
                    <Tag color="default">
                        Players: {playerLabels.join(", ")}
                    </Tag>
                }
            </Row>

            <Row gutter={16} style={{ marginBottom: '16px', alignItems: 'center' }}>
                <Col span={14}>
                    <Select
                        showSearch
                        allowClear
                        notFoundContent="No drills found for the selected filters."
                        placeholder="Please select a drill to add to the plan"
                        filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        value={selectedDrill}
                        style={{ width: "100%" }}
                        onChange={(val) => setSelectedDrill(val)}
                        options={drills?.map((item) => {
                            return {
                                label: item.name + (drillData.find((e) => e.drill_id === item.drill_id)?.latest_date ? " ( Latest: " + drillData.find((e) => e.drill_id === item.drill_id)?.latest_date + " )" : ""),
                                value: item.drill_id,
                            };
                        })}
                    />
                </Col>
                <Col>
                    <Text>Duration:</Text>
                </Col>
                <Col span={3}>
                    <InputNumber
                        min={1}
                        value={selectedDuration}
                        size="small"
                        style={{ width: "100%" }}
                        onChange={(value) => setSelectedDuration(value ?? 1)}
                    />
                </Col>
                <Col>
                    <Button
                        type="primary"
                        size="small"
                        disabled={!(selectedDrill && selectedDuration)}
                        onClick={handleAddPlan}
                    >
                        Add to Plan
                    </Button>
                </Col>
            </Row>

            <Row>
                <Col span={24}>
                    <PlanTable
                        plan={plan}
                        setPlan={setPlan}
                        drills={drills}
                        metrics={teamMetrics || []}
                        costPerMin={costPerMin}
                        teamInfo={teamInfo}
                        handleRemovePlan={handleRemovePlan}
                    />
                </Col>
            </Row>
        </div>
    )
}

interface ISecondStep {
    startDate: Dayjs
    endDate: Dayjs
    days: string[]
    players: string[]
    playerLabels: string[]
    plan: { drill_id: number, duration: number }[]
    setPlan: React.Dispatch<React.SetStateAction<{ drill_id: number, duration: number }[]>>
    setDrills: React.Dispatch<React.SetStateAction<{ drill_id: number, name: string }[]>>
    setCostPerMin: React.Dispatch<React.SetStateAction<any[]>>
    drillData: { drill_id: number, team_id_id: number, latest_date: string, name: string }[]
}

type DrillCostPerMinute = {
    "avg_accel_load": number,
    "decel": number,
    "distance_per_minute": number,
    "distance": number,
    "drill_id__name": string,
    "drill_id": number,
    "high_speed_dist": number,
    "high_speed_running_per_min": number,
    "mech_intensity": number,
    "mech_loads": number,
    "physio_intensity": number,
    "physio_loads": number,
    "player_load_per_minute": number,
    "player_load": number,
    "top_speed_percent": number,
    "top_speed": number,
};

export default SecondStep
